import React, {memo, useContext, useEffect, useState} from 'react';
import cn from 'classnames';
import {useSelector} from 'react-redux';
import {ClockType} from '@/common/constants/units';
import {getLocalOffsetFromUTCOffset} from '@/common/helpers/date/getLocalOffsetFromUTCOffset';
import {useLocalizer} from '@/common/services/localizer';
import {selectClockType} from '@/app/store/config/selectors';
import {ServicesContext} from '@/app/contexts/ServicesContext';
import styles from './WeatherTimezone.css';

const getDisplayTime = (date: Date, clockType: ClockType) => {
	return date
		.toLocaleTimeString('en', {
			minute: 'numeric',
			hour: 'numeric',
			hour12: clockType === ClockType['12h'],
		})
		.toLowerCase();
};

const getHourAngle = (hours: number, minutes: number) => {
	const hoursOfPeriod = hours >= 12 ? hours - 12 : hours;
	const totalMinutes = hoursOfPeriod * 60 + minutes;
	const progress = totalMinutes / (60 * 12);
	return 360 * progress;
};

const getMinuteAngle = (minutes: number) => {
	const progress = minutes / 60;
	return 360 * progress;
};

interface WeatherTimezoneProps {
	className?: string;
	targetTitle: string;
	targetTitleLc: string;

	/**
	 * Target location timezone offset to UTC
	 */
	targetTimezoneOffset: number;
}

export const WeatherTimezone = memo<WeatherTimezoneProps>(props => {
	const {className, targetTitle, targetTitleLc, targetTimezoneOffset} = props;
	const clockType = useSelector(selectClockType);

	const {wakeUpDetector} = useContext(ServicesContext)!;
	const t = useLocalizer();

	const [localDate, setLocalDate] = useState(() => new Date());
	const [shown, setShown] = useState(false);

	const toLocalTimezoneOffset = getLocalOffsetFromUTCOffset(targetTimezoneOffset);
	const targetDate = new Date(localDate.getTime() + toLocalTimezoneOffset);

	// Show or hide mismatched with the user-current timezone
	useEffect(() => {
		if (!toLocalTimezoneOffset) {
			if (shown) {
				setShown(false);
			}
			return;
		}

		setShown(true);
	}, [toLocalTimezoneOffset]);

	// Auto update time in each minute
	useEffect(() => {
		if (!shown) {
			return;
		}

		const updateInterval = 60 * 1000; // One minute
		const intervalId = window.setInterval(() => {
			setLocalDate(new Date());
		}, updateInterval);

		return () => {
			clearInterval(intervalId);
		};
	}, [shown]);

	// Update time after wake-up; This fixes Safari timeouts stack problem after wakeup
	useEffect(() => {
		if (!shown || !wakeUpDetector) {
			return;
		}

		return wakeUpDetector.onWakeUp.on(() => {
			setLocalDate(new Date());
		});
	}, [shown]);

	// Disallow render on ssr to avoid mismatch warning when hydrate on client
	if (!shown) {
		return null;
	}

	const localTime = getDisplayTime(localDate, clockType);
	const localHours = localDate.getHours();
	const localMinutes = localDate.getMinutes();
	const localWeekday = localDate.toLocaleDateString(t.language, {weekday: 'long'});

	const targetTime = getDisplayTime(targetDate, clockType);
	const targetHours = targetDate.getHours();
	const targetMinutes = targetDate.getMinutes();
	const targetWeekday = targetDate.toLocaleDateString(t.language, {weekday: 'long'});

	return (
		<div className={cn(styles.root, className)}>
			<div className={styles.main}>
				<div className={styles.clock}>
					<p className={styles.title}>{t('localTime')}</p>
					<div className={styles.hand} style={{transform: `rotate(${getMinuteAngle(localMinutes)}deg)`}} />
					<div
						className={cn(styles.hand, styles.isHour)}
						style={{transform: `rotate(${getHourAngle(localHours, localMinutes)}deg)`}}
					/>
					<p className={styles.time}>{localTime}</p>
				</div>
				<div className={styles.clock}>
					<p className={styles.title}>{targetTitle}</p>
					<div className={styles.hand} style={{transform: `rotate(${getMinuteAngle(targetMinutes)}deg)`}} />
					<div
						className={cn(styles.hand, styles.isHour)}
						style={{transform: `rotate(${getHourAngle(targetHours, targetMinutes)}deg)`}}
					/>
					<p className={styles.time}>{targetTime}</p>
				</div>
			</div>
			{localWeekday !== targetWeekday && (
				<p className={styles.extraInfo}>
					{t('nowWeekday', {
						location: targetTitleLc,
						weekday: targetWeekday,
					})}
				</p>
			)}
		</div>
	);
});

WeatherTimezone.displayName = 'WeatherTimezone';
